import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api, { createDemoSession } from '../lib/api'
import { isDemoMode } from '../lib/demoMode'

interface User {
  id: string
  email: string
  name: string | null
  phone?: string | null
  avatarUrl: string | null
  emailVerified: boolean
  isShopbayAdmin: boolean
  impersonation?: {
    active: boolean
    adminUser: {
      id: string
      email: string
      name: string | null
    }
  } | null
}

interface Shop {
  id: string
  name: string
  role: string
}

interface AuthState {
  user: User | null
  shops: Shop[]
  currentShopId: string | null
  isLoading: boolean
  isAuthenticated: boolean
  
  // Actions
  setUser: (user: User | null, shops: Shop[]) => void
  setCurrentShop: (shopId: string) => void
  setLoading: (loading: boolean) => void
  login: (email: string, code: string) => Promise<void>
  loginDemo: () => Promise<string>
  logout: () => Promise<void>
  fetchUser: () => Promise<void>
}

const demoUserId = (import.meta.env.VITE_DEMO_USER_ID as string | undefined)?.trim()
const demoUserEmail = (import.meta.env.VITE_DEMO_USER_EMAIL as string | undefined)?.trim() || 'demo@shopbay.local'
const demoShopId = (import.meta.env.VITE_DEMO_SHOP_ID as string | undefined)?.trim()
const demoShopName = (import.meta.env.VITE_DEMO_SHOP_NAME as string | undefined)?.trim() || 'Shopbay Demo'
const isDemoAuthEnabled = isDemoMode && Boolean(demoUserId && demoShopId)

function buildDemoUser(): User {
  return {
    id: demoUserId!,
    email: demoUserEmail,
    name: 'Demo Owner',
    avatarUrl: null,
    emailVerified: true,
    isShopbayAdmin: false,
    impersonation: null,
  }
}

function buildDemoShops(): Shop[] {
  return [
    {
      id: demoShopId!,
      name: demoShopName,
      role: 'owner',
    },
  ]
}

function resolveCurrentShopId(currentShopId: string | null, shops: Shop[]): string | null {
  if (shops.length === 0) {
    return null
  }

  if (shops.length === 1) {
    return shops[0].id
  }

  const hasCurrentShop = currentShopId
    ? shops.some((shop) => shop.id === currentShopId)
    : false

  return hasCurrentShop ? currentShopId : shops[0].id
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: isDemoAuthEnabled ? buildDemoUser() : null,
      shops: isDemoAuthEnabled ? buildDemoShops() : [],
      currentShopId: isDemoAuthEnabled ? demoShopId! : null,
      isLoading: !isDemoAuthEnabled,
      isAuthenticated: isDemoAuthEnabled,

      setUser: (user, shops) => {
        if (isDemoAuthEnabled) {
          set({
            user: buildDemoUser(),
            shops: buildDemoShops(),
            currentShopId: demoShopId!,
            isAuthenticated: true,
            isLoading: false,
          })
          return
        }

        set({ 
          user, 
          shops,
          isAuthenticated: !!user,
          currentShopId: resolveCurrentShopId(get().currentShopId, shops)
        })
      },

      setCurrentShop: (shopId) => {
        set({ currentShopId: shopId })
      },

      setLoading: (loading) => {
        set({ isLoading: loading })
      },

      login: async (email, code) => {
        if (isDemoAuthEnabled) {
          set({
            user: buildDemoUser(),
            shops: buildDemoShops(),
            currentShopId: get().currentShopId || demoShopId!,
            isAuthenticated: true,
            isLoading: false,
          })
          return
        }

        await api.post('/auth/email/verify-otp', { email, code })
        
        // After successful login, fetch user with shops
        await get().fetchUser()
        if (!get().isAuthenticated) {
          throw new Error('SESSION_NOT_ESTABLISHED')
        }
      },

      loginDemo: async () => {
        if (isDemoAuthEnabled) {
          set({
            user: buildDemoUser(),
            shops: buildDemoShops(),
            currentShopId: get().currentShopId || demoShopId!,
            isAuthenticated: true,
            isLoading: false,
          })
          return '/vehicles'
        }

        const data = await createDemoSession()
        await get().fetchUser()
        if (!get().isAuthenticated) {
          throw new Error('SESSION_NOT_ESTABLISHED')
        }
        return typeof data?.redirectPath === 'string' ? data.redirectPath : '/vehicles'
      },

      logout: async () => {
        try {
          await api.post('/auth/logout')
        } catch (e) {
          console.error('Logout error:', e)
        }
        set({ 
          user: null, 
          shops: [], 
          currentShopId: null, 
          isAuthenticated: false 
        })
      },

      fetchUser: async () => {
        if (isDemoAuthEnabled) {
          set({
            user: buildDemoUser(),
            shops: buildDemoShops(),
            currentShopId: get().currentShopId || demoShopId!,
            isAuthenticated: true,
            isLoading: false,
          })
          return
        }

        try {
          set({ isLoading: true })
          const { data } = await api.get('/auth/me')
          
          if (data.user) {
            set({
              user: {
                ...data.user,
                isShopbayAdmin: Boolean(data.user.isShopbayAdmin),
                impersonation: data.user.impersonation
                  ? {
                      active: Boolean(data.user.impersonation.active),
                      adminUser: data.user.impersonation.adminUser,
                    }
                  : null,
              },
              shops: data.shops,
              isAuthenticated: true,
              isLoading: false,
              currentShopId: resolveCurrentShopId(get().currentShopId, data.shops)
            })
          } else {
            set({ 
              user: null, 
              shops: [], 
              isAuthenticated: false, 
              isLoading: false 
            })
          }
        } catch (e) {
          set({ 
            user: null, 
            shops: [], 
            isAuthenticated: false, 
            isLoading: false 
          })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        currentShopId: state.currentShopId 
      }),
    }
  )
)
